import React from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DateInfoTooltip, { DateInfoTooltipProps } from 'components/Tooltip/DateInfoTooltip';
import WidgetMoreActionMenu, { MoreOptionMenuProps } from 'components/Dropdown/WidgetMoreActionMenu';
import { ReactComponent as ToolTipIcon } from 'assets/PepWise2.0/InfoIcon.svg';
import LoaderComponent from 'components/Loader';
import { useTheme } from '@mui/material/styles';

interface SlaStatusCardProps {
    projectName: string;
    slaTime: string;
    slaStatus: string;
    isProjectNameLoading?: boolean;
    isSlaTimeLoading?: boolean;
    isSlaStatusLoading?: boolean;
    slaTimeAction?: React.ReactNode;
    isEditEnabled?: boolean;
    infoTooltip?: Omit<DateInfoTooltipProps, 'children'>;
    infoOptionMenu?: MoreOptionMenuProps;
}

const SlaStatusCard: React.FC<SlaStatusCardProps> = ({
    projectName,
    slaTime,
    slaStatus,
    isProjectNameLoading,
    isSlaTimeLoading,
    isSlaStatusLoading,
    slaTimeAction,
    isEditEnabled,
    infoTooltip,
    infoOptionMenu,
}) => {
    const theme = useTheme();
    const colors = theme.palette;
    const getStatusColor = (status: string) => {
        const upperStatus = status.toUpperCase();

        if (upperStatus.includes('MISSED')) {
            return colors.widget.sla.missed.bg;
        }

        if (upperStatus.includes('MET')) {
            return colors.widget.sla.met.bg;
        }

        if (upperStatus.includes('NOT STARTED')) {
            return colors.widget.sla.scheduled.bg;
        }

        if (upperStatus.includes('IN-PROGRESS') || upperStatus.includes('IN PROGRESS')) {
            return colors.widget.sla.progress.bg;
        }

        return colors.boxColor.pgmGray.bgColor;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 3,
                bgcolor: colors.widget.background.card,
                p: '16px 24px',
                borderRadius: '4px',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #E0E0E0',
                mb: 2,
                width: '100%',
                minWidth: 0,
                height: '100%',
                boxSizing: 'border-box'
            }}
        >
            <Stack spacing={0.5} sx={{ minWidth: '150px' }}>
                <Typography variant="caption" sx={{ color: colors.widget.text.noChange, fontWeight: 500, fontSize: '14px', }}>
                    Project Name
                </Typography>
                {isProjectNameLoading ? (
                    <LoaderComponent size={24} />
                ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: colors.widget.text.value }}>
                        {projectName}
                    </Typography>
                )}
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: '100px' }}>
                <Typography variant="caption" sx={{ color: colors.widget.text.noChange, fontWeight: 500, fontSize: '14px', }}>
                    SLA Time
                </Typography>
                {isSlaTimeLoading ? (
                    <LoaderComponent size={24} />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: colors.widget.text.value }}>
                            {slaTime}
                        </Typography>
                        {slaTimeAction}
                    </Box>
                )}
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: 'fit-content', pr: 4, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: colors.widget.text.noChange, fontWeight: 500, fontSize: '14px', textAlign: 'center' }}>
                    SLA Status
                </Typography>
                {isSlaStatusLoading ? (
                    <LoaderComponent size={24} />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Chip
                            label={slaStatus}
                            sx={{
                                backgroundColor: `${getStatusColor(slaStatus)} !important`,
                                color: `${colors.widget.chart.labelColor} !important`,
                                borderRadius: '16px',
                                height: '30px',
                                minWidth: '105px',
                                '& .MuiChip-label': {
                                    px: 4,
                                    py: 0,
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'clip',
                                    overflow: 'visible',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    lineHeight: '25px',
                                    letterSpacing: '0%',
                                    verticalAlign: 'middle',
                                    textTransform: 'uppercase'
                                }
                            }}
                        />
                    </Box>
                )}
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
                {!!infoTooltip && (
                    <DateInfoTooltip {...infoTooltip}>
                        <ToolTipIcon />
                    </DateInfoTooltip>
                )}
                {!!infoOptionMenu && isEditEnabled && (
                    <WidgetMoreActionMenu
                        {...infoOptionMenu}
                        icon={<MoreVertIcon sx={{ color: `${colors.widget.text.action.primary} !important` }} />}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SlaStatusCard;
